import { useMemo } from 'react';

import { usePDPContext } from '../context/static/PDPContext';
import { useStaticContext } from '../context/static/StaticContext';
import type { CarouselImageProperty } from '~/components/pdp/productDetail/productImageGallery/ProductImage';

/**
 * TODO: The logic of this hook potentially can be moved to
 * GraphQL product query, instead of fetching the entire `simplifiedImages` object
 */
export const useProductDetailsImages = (ordering?: string) => {
  const { configuration: { allowedPDPImageTypes } = {} } = useStaticContext();

  const allowedPDPImageTypesNo09Images = useMemo(
    () =>
      allowedPDPImageTypes
        ?.split(',')
        .filter(el => !el.includes('09'))
        .join(','),
    [allowedPDPImageTypes]
  );

  const { product: { simplifiedImages } = {} } = usePDPContext();
  const imageTypes = useMemo(
    () => ((ordering || allowedPDPImageTypesNo09Images) ?? '').split(','),
    [ordering, allowedPDPImageTypesNo09Images]
  );

  const imagesMap = useMemo(
    () => new Map(simplifiedImages?.entry?.map(image => [image?.key, image?.value])),
    [simplifiedImages?.entry]
  );

  const videoIndex = useMemo(() => {
    const index = imageTypes.findIndex(type => type?.toLowerCase() === 'video');

    return index === -1 ? undefined : index;
  }, [imageTypes]);

  const productImageList = useMemo((): CarouselImageProperty[] => {
    const finalImages = imageTypes
      .map(imageProfileList =>
        imageProfileList
          .split('|')
          .map(profile => {
            const normalizedProfile = profile.trim();
            const original = imagesMap.get(normalizedProfile);

            return {
              original: { ...original, profile: normalizedProfile },
            } as CarouselImageProperty;
          })
          .find(image => image.original.url !== undefined)
      )
      .filter(image => image !== undefined);

    return finalImages;
  }, [imageTypes, imagesMap]);

  const value = useMemo(
    () => ({
      productImageList,
      image01: productImageList.find(image => image.original.profile?.endsWith('01'))?.original,
      image06: productImageList.find(image => image.original.profile?.endsWith('06'))?.original,
      image07: productImageList.find(image => image.original.profile?.endsWith('07'))?.original,
      videoIndex,
    }),
    [productImageList, videoIndex]
  );

  return value;
};
